import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";
import {
  TrendingUp, ChevronDown, ChevronRight, Check, Clock, Info, AlertTriangle,
  Boxes, History, Sparkles, GitBranch, ArrowUpRight,
} from "lucide-react";
import {
  TONE, MOVEMENT, STATUS, evidenceInfo, conceptName, shortTitle,
  EVIDENCE_TOOLTIP, GROUPING_TOOLTIP, CENSORED_TOOLTIP, HISTORY_TOOLTIP,
} from "@/lib/breakoutLabels";

const InfoDot = ({ text }) => (
  <span title={text} className="inline-flex cursor-help text-zinc-500 hover:text-zinc-300 transition-colors">
    <Info size={13} />
  </span>
);

const Chip = ({ tone, children, title }) => (
  <span title={title} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${TONE[tone] || TONE.neutral}`}>
    {children}
  </span>
);

// rank sparkline (lower rank = higher on chart, so we invert)
function Sparkline({ ranks, w = 132, h = 30 }) {
  const pts = (ranks || []).filter((r) => r != null);
  if (pts.length < 2) return <span className="text-xs text-zinc-500">{pts.length === 1 ? `#${pts[0]}` : "—"}</span>;
  const max = Math.max(...pts), min = Math.min(...pts);
  const span = max - min || 1;
  const step = w / (pts.length - 1);
  const coords = pts.map((r, i) => [i * step, h - ((max - r) / span) * (h - 6) - 3]);
  const d = coords.map((c, i) => `${i ? "L" : "M"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const up = pts[pts.length - 1] <= pts[0];
  const stroke = up ? "#34d399" : "#a1a1aa";
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r={i === coords.length - 1 ? 2.6 : 1.4} fill={stroke} />
      ))}
    </svg>
  );
}

const SIGNAL_TONE = {
  HOLDING_STEADY: { cls: "text-zinc-400 bg-zinc-800/60 border-zinc-700", dot: "bg-zinc-500" },
  WATCH: { cls: "text-sky-300 bg-sky-400/10 border-sky-400/25", dot: "bg-sky-400" },
  INTERESTING: { cls: "text-violet-300 bg-violet-400/10 border-violet-400/25", dot: "bg-violet-400" },
  STRONG_AMAZON_SIGNAL: { cls: "text-emerald-300 bg-emerald-400/10 border-emerald-400/25", dot: "bg-emerald-400" },
  INVESTIGATE: { cls: "text-emerald-200 bg-emerald-500/15 border-emerald-400/40", dot: "bg-emerald-400" },
  CROSS_SOURCE_CANDIDATE: { cls: "text-rose-200 bg-rose-500/15 border-rose-400/45", dot: "bg-rose-400" },
};

function GoogleCheck({ c, reload }) {
  const [open, setOpen] = useState(false);
  const [queries, setQueries] = useState([conceptName(c.canonical_concept)]);
  const [busy, setBusy] = useState(false);
  const cs = c.cross_source;

  const run = async () => {
    const qs = queries.map((q) => q.trim()).filter(Boolean).slice(0, 3);
    if (!qs.length) { toast.error("Enter at least one precise subtype query"); return; }
    setBusy(true);
    try {
      // OFFLINE mocked dry-run (neutral flat sample) — live Google Trends stays gated until approved
      const mock = Array.from({ length: 53 }, () => 40);
      const r = await api.post("/amazon/cross-source/check", { concept: c.canonical_concept, queries: qs, mock_series: mock, live: false });
      toast.success(`Search check (mock): ${r.verdict.replaceAll("_", " ").toLowerCase()}`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="px-5 pb-5 -mt-1" data-testid={`gt-panel-${c.canonical_concept}`}>
      <div className="rounded-lg border border-[#232326] bg-[#0e0e10] p-3.5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-zinc-400">
            Google search signal: {cs?.verdict
              ? <span className="text-zinc-100 font-medium">{cs.verdict.replaceAll("_", " ").toLowerCase()}{cs.data_origin === "mock" ? " (mock)" : ""}</span>
              : <span className="text-zinc-500">not checked yet</span>}
          </div>
          <button data-testid={`gt-open-${c.canonical_concept}`} onClick={() => setOpen((o) => !o)} className="text-xs font-medium text-sky-300 border border-sky-400/25 bg-sky-400/10 rounded-md px-2.5 py-1.5 hover:bg-sky-400/15 transition-colors">
            Check Google Search
          </button>
        </div>
        {open && (
          <div className="mt-3 space-y-2">
            <div className="text-[11px] text-amber-300 flex items-center gap-1.5"><Info size={12} /> Live Google Trends is gated OFF — this runs a mocked dry-run of the workflow until you approve the first candidate.</div>
            {queries.map((q, i) => (
              <input key={i} data-testid={`gt-query-${c.canonical_concept}-${i}`} value={q} onChange={(e) => setQueries((qq) => qq.map((x, j) => j === i ? e.target.value : x))}
                placeholder="Precise subtype query (e.g. no shock bark collar)" className="w-full bg-[#09090B] border border-[#27272A] rounded-md px-2.5 py-1.5 text-xs text-zinc-100 focus:border-sky-500/50 outline-none" />
            ))}
            <div className="flex items-center gap-2">
              {queries.length < 3 && <button onClick={() => setQueries((q) => [...q, ""])} className="text-xs text-zinc-400 hover:text-zinc-200">+ add query</button>}
              <button data-testid={`gt-run-${c.canonical_concept}`} disabled={busy} onClick={run} className="text-xs font-medium bg-sky-500 text-[#09090B] rounded-md px-3 py-1.5 hover:bg-sky-400 disabled:opacity-50 transition-colors">
                {busy ? "Running…" : "Run mocked dry-run"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPillGate() { return null; }

function CurrentSignal({ s }) {
  const t = SIGNAL_TONE[s.stage] || SIGNAL_TONE.HOLDING_STEADY;
  return (
    <div data-testid={`signal-${s.stage}`} className={`rounded-lg border p-3.5 ${t.cls}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">Current signal</span>
        <span className="text-sm font-semibold">{s.label}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2.5 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Why</div>
          <ul className="text-zinc-300 space-y-0.5">{s.why.map((w, i) => <li key={i}>· {w}</li>)}</ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{s.is_blocking ? "Blocking" : "What's missing"}</div>
          <ul className="text-zinc-400 space-y-0.5">{s.missing.map((m, i) => <li key={i}>· {m}</li>)}</ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Next action</div>
          <div className="text-zinc-200 font-medium">{s.next_action}</div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.INSUFFICIENT_HISTORY;
  const Icon = s.icon === "check" ? Check : Clock;
  return (
    <span title={s.info} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${TONE[s.tone]}`}>
      <Icon size={12} className={s.icon === "check" ? "text-emerald-400" : "text-zinc-400"} />
      {s.label}
    </span>
  );
}

function SummaryCard({ label, value, tone = "neutral", tip, icon: Icon }) {
  return (
    <div data-testid={`summary-${label}`} className="bg-[#18181B] border border-[#27272A] rounded-xl p-5 flex flex-col gap-2 hover:border-zinc-700 transition-colors">
      <div className="text-sm font-medium text-zinc-400 flex items-center justify-between">
        <span className="flex items-center gap-1.5">{label} {tip && <InfoDot text={tip} />}</span>
        {Icon && <Icon size={16} className="text-zinc-600" />}
      </div>
      <div className={`text-3xl font-bold tracking-tight ${tone === "positive" ? "text-emerald-400" : tone === "review" ? "text-amber-400" : tone === "info" ? "text-sky-400" : "text-zinc-50"}`}>{value}</div>
    </div>
  );
}

function ListingRow({ a, conceptFirstDay }) {
  const ranks = (a.rank_by_date || []).map((x) => x.rank);
  const current = ranks.length ? ranks[ranks.length - 1] : null;
  let badge;
  if (a.climbing) badge = <Chip tone="positive"><ArrowUpRight size={12} /> Gaining rank</Chip>;
  else if (conceptFirstDay && a.first_seen > conceptFirstDay) badge = <Chip tone="info"><Sparkles size={12} /> Recently appeared</Chip>;
  else badge = <Chip tone="neutral">Holding steady</Chip>;
  return (
    <div data-testid={`listing-${a.asin}`} className="flex gap-4 p-3 rounded-lg border border-[#232326] bg-[#0e0e10]">
      <div className="w-14 h-14 shrink-0 rounded-md border border-[#27272A] bg-[#141416] flex items-center justify-center overflow-hidden">
        {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-contain" /> : <Boxes size={16} className="text-zinc-700" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-zinc-200 leading-snug line-clamp-2">{a.title}</div>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
          <span>Rank now <span className="text-zinc-200 font-medium">#{current ?? "—"}</span></span>
          <span>tracked {a.observation_count} day{a.observation_count === 1 ? "" : "s"}</span>
          {a.censored_dates?.length > 0 && (
            <span className="flex items-center gap-1">Outside Top 30: {a.censored_dates.length}d <InfoDot text={CENSORED_TOOLTIP} /></span>
          )}
          {badge}
        </div>
        <div className="mt-2"><Sparkline ranks={ranks} /></div>
      </div>
    </div>
  );
}

function ConceptDetail({ cid }) {
  const [d, setD] = useState(null);
  const [tech, setTech] = useState(false);
  useEffect(() => { api.get("/amazon/concept-detail", { params: { concept: cid } }).then((r) => setD(r.data)); }, [cid]);
  if (!d) return <div className="border-t border-[#27272A] p-6 text-sm text-zinc-500">Loading breakdown…</div>;
  const cl = d.concept_level;
  // cumulative listings tracked over time
  let cum = 0;
  const growth = cl.daily.map((r) => { cum += r.new_asins; return cum; });

  return (
    <div className="border-t border-[#27272A] bg-[#09090B]/50 p-6 flex flex-col gap-8">
      {cl.possible_overbroad_concept && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-400/20 bg-amber-400/10 px-4 py-3">
          <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-300">Grouping needs review</div>
            <div className="text-xs text-zinc-400 mt-0.5">Some listings may represent different versions or product types. Check the individual listings before trusting the combined trajectory.</div>
          </div>
        </div>
      )}

      {/* CONCEPT LEVEL */}
      <div className="flex flex-col gap-4">
        <div className="text-xs font-medium text-zinc-500 uppercase tracking-widest flex items-center gap-2"><GitBranch size={13} /> Is this product idea spreading?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="rounded-lg border border-[#232326] bg-[#0e0e10] p-4">
            <div className="text-xs text-zinc-500 mb-2">Best Amazon rank over time</div>
            <Sparkline ranks={cl.daily.map((r) => r.best_rank)} w={220} h={44} />
            <div className="text-xs text-zinc-500 mt-2">Best <span className="text-zinc-200">#{Math.min(...cl.daily.filter((r) => r.best_rank != null).map((r) => r.best_rank))}</span> · tracked {cl.daily.length} days</div>
          </div>
          <div className="rounded-lg border border-[#232326] bg-[#0e0e10] p-4">
            <div className="text-xs text-zinc-500 mb-2">Listings tracked over time</div>
            <div className="text-sm text-zinc-200 font-medium">{growth.join(" → ")}</div>
            <div className="text-xs text-zinc-500 mt-2">{cl.unique_asins} total listings · {cl.category_spread.join(", ")}</div>
          </div>
        </div>
      </div>

      {/* ASIN LEVEL */}
      <div className="flex flex-col gap-3">
        <div className="text-xs font-medium text-zinc-500 uppercase tracking-widest flex items-center gap-2"><Boxes size={13} /> Which exact Amazon products are moving? ({d.asin_level.length})</div>
        <div className="flex flex-col gap-2">
          {d.asin_level.map((a) => <ListingRow key={a.asin} a={a} conceptFirstDay={cl.first_day} />)}
        </div>
      </div>

      {/* TECHNICAL */}
      <div>
        <button data-testid={`tech-toggle-${cid}`} onClick={() => setTech((t) => !t)} className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 transition-colors">
          {tech ? <ChevronDown size={13} /> : <ChevronRight size={13} />} View technical details
        </button>
        {tech && (
          <div className="mt-3 rounded-lg border border-[#232326] bg-[#0e0e10] p-4 text-[11px] font-mono text-zinc-400 space-y-1 overflow-x-auto">
            <div>canonical_product_id: <span className="text-zinc-300">{d.canonical_concept}</span></div>
            <div>trajectory_status: {d.trajectory_status} · eligibility: {d.eligibility_version}</div>
            <div>evidence_grade: {d.evidence_grade} · maximum_defensible_confidence: {d.maximum_defensible_confidence}</div>
            <div>movement: {cl.movement.join(", ")} · climbing_asins: {cl.climbing_asin_count} · recent_new: {cl.recent_new_asin_count}</div>
            <div>persistence: {cl.persistence.observed_days}/{cl.persistence.collection_days} ({cl.persistence.ratio})</div>
            {cl.possible_overbroad_concept && <div>overbroad_terms: {cl.overbroad_differing_terms.join(", ")}</div>}
            <div className="pt-2 border-t border-[#232326] mt-2">listings:</div>
            {d.asin_level.map((a) => (
              <div key={a.asin}>{a.asin} · conf={a.resolver_confidence} · obs={a.observation_count} · censored={a.censored_dates.length} · net={a.net_rank_improvement}{a.human_confirmed ? " · human_confirmed" : ""}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TrajectoryBoard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState({});
  const load = () => api.get("/amazon/trajectory-board").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  const total = d.total;
  const eligible = d.concepts.filter((c) => c.trajectory_status === "EARLY_TRAJECTORY_ELIGIBLE").length;
  const newListings = d.concepts.filter((c) => (c.movement || []).includes("NEW_ASINS_APPEARING")).length;
  const needsGrouping = d.concepts.filter((c) => c.possible_overbroad_concept).length;

  return (
    <div className="p-8 max-w-[1280px] mx-auto" data-testid="trajectory-board-page">
      <div className="flex items-center gap-2.5 mb-1.5">
        <TrendingUp className="text-emerald-400" size={22} />
        <h1 className="font-head text-2xl font-bold tracking-tight text-zinc-50">Trajectory Board</h1>
        <span title={HISTORY_TOOLTIP} className="ml-1"><InfoDot text={"How trajectory works: " + HISTORY_TOOLTIP} /></span>
      </div>
      <p className="text-sm text-zinc-400 mb-8 max-w-2xl">
        See which product ideas are gaining traction on Amazon over time. We track daily New Releases activity and look for products that persist, climb in rank, or attract new listings.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <SummaryCard label="Products tracked" value={total} icon={Boxes} />
        <SummaryCard label="Enough history to analyze" value={eligible} icon={History} tip={STATUS.EARLY_TRAJECTORY_ELIGIBLE.info} />
        <SummaryCard label="New listings appearing" value={newListings} tone="info" icon={Sparkles} tip={MOVEMENT.NEW_ASINS_APPEARING.info} />
        <RouterLink to="/grouping" data-testid="link-grouping-review">
          <SummaryCard label="Needs grouping review" value={needsGrouping} tone="review" icon={AlertTriangle} tip={GROUPING_TOOLTIP} />
        </RouterLink>
      </div>

      {d.concepts.length === 0 ? (
        <div className="border border-dashed border-[#27272A] rounded-xl p-8 text-center text-sm text-zinc-500">No products tracked yet.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {d.concepts.map((c) => {
            const isOpen = open[c.canonical_concept];
            const ev = evidenceInfo(c.evidence_grade);
            const tracked = c.best_rank_by_day?.length || c.span_calendar_days + 1;
            return (
              <div key={c.canonical_concept} data-testid={`traj-${c.canonical_concept}`} className="bg-[#18181B] border border-[#27272A] rounded-xl overflow-hidden transition-all duration-200 hover:border-zinc-700">
                <button
                  data-testid={`traj-toggle-${c.canonical_concept}`}
                  onClick={() => setOpen((o) => ({ ...o, [c.canonical_concept]: !o[c.canonical_concept] }))}
                  className="w-full text-left p-5 flex flex-col lg:flex-row lg:items-center gap-4 hover:bg-[#1c1c1f]/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      {isOpen ? <ChevronDown size={16} className="text-zinc-500 shrink-0" /> : <ChevronRight size={16} className="text-zinc-500 shrink-0" />}
                      <span className="text-base font-semibold text-zinc-100 capitalize">{conceptName(c.canonical_concept)}</span>
                      <span className="text-xs text-zinc-500">{c.unique_asins} listing{c.unique_asins === 1 ? "" : "s"} · tracked {tracked} day{tracked === 1 ? "" : "s"}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-2.5 ml-6">
                      <StatusPill status={c.trajectory_status} />
                      {(c.movement || []).map((m) => {
                        const mm = MOVEMENT[m] || MOVEMENT.STABLE;
                        return <Chip key={m} tone={mm.tone} title={mm.info}>{mm.label}</Chip>;
                      })}
                      {c.possible_overbroad_concept && (
                        <Chip tone="review" title={GROUPING_TOOLTIP}><AlertTriangle size={12} /> Grouping needs review</Chip>
                      )}
                    </div>
                    {c.current_signal && (
                      <div className="mt-3 ml-6"><CurrentSignal s={c.current_signal} /></div>
                    )}
                    {c.best_rank_by_day?.length > 1 && (
                      <div className="mt-3 ml-6 flex items-center gap-3">
                        <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Best rank</span>
                        <Sparkline ranks={c.best_rank_by_day.map((x) => x.rank)} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-5 ml-6 lg:ml-0 shrink-0">
                    <div className="text-right">
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1 justify-end">Evidence <InfoDot text={EVIDENCE_TOOLTIP} /></div>
                      <div className={`text-sm font-medium ${TONE[ev.tone].split(" ")[0]}`}>{ev.label}{ev.detail ? ` — ${ev.detail}` : ""}</div>
                    </div>
                    <span className="text-xs font-medium text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
                      View {c.unique_asins} listing{c.unique_asins === 1 ? "" : "s"} <ArrowUpRight size={14} />
                    </span>
                  </div>
                </button>
                {["INVESTIGATE", "CROSS_SOURCE_CANDIDATE"].includes(c.current_signal?.stage) && (
                  <GoogleCheck c={c} reload={load} />
                )}
                {isOpen && <ConceptDetail cid={c.canonical_concept} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
